import React, { useState, useEffect, useRef } from 'react';
import './TimelineGantt.css';

function TimelineGantt({ tasks, onTaskClick, onTaskDateChange, onItemDrop, onAssignResource, companies, companyId }) {
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contextMenu, setContextMenu] = useState(null);
  const [resources, setResources] = useState([]);
  const [hideWeekends, setHideWeekends] = useState(false);
  
  // Single drag state for ONE task at a time
  const [dragState, setDragState] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [isExternalDragging, setIsExternalDragging] = useState(false);
  const scrollDeltaRef = useRef(0);
  const ganttContainerRef = useRef(null);
  const timelineHeaderRef = useRef(null);

  // Generate date columns based on view mode
  // Use a wider rolling window so horizontal scrolling is seamless (no jump navigation)
  const generateDateColumns = () => {
    const columns = [];
    const totalDays = viewMode === 'month' ? 84 : 56; // 12 weeks for Month view, 8 weeks for 4-weeks view

    // Start on the Sunday of the week containing currentDate to align weeks
    const start = new Date(currentDate);
    start.setHours(0, 0, 0, 0); // Normalize to midnight
    start.setDate(currentDate.getDate() - currentDate.getDay());

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    let lastMonth = null;

    for (let i = 0; i < totalDays; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      date.setHours(0, 0, 0, 0); // Normalize to midnight
      
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const showMonthLabel = monthName !== lastMonth;
      lastMonth = monthName;
      
      const dateStr = date.toISOString().split('T')[0];
      
      columns.push({
        date,
        label: date.getDate().toString(),
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        monthName,
        showMonthLabel,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: dateStr === todayStr,
      });
    }

    return columns;
  };

  let dateColumns = generateDateColumns();
  
  if (hideWeekends) {
    dateColumns = dateColumns.filter(col => !col.isWeekend);
  }

  const visibleStart = dateColumns[0]?.date;
  const visibleEnd = dateColumns[dateColumns.length - 1]?.date;
  const MS_DAY = 1000 * 60 * 60 * 24;
  const columnWidth = 64; // px per day for denser view

  // Row ordering
  const rowOrderKey = companyId ? `ganttRowOrder_${companyId}` : 'ganttRowOrder_default';
  const [rowOrder, setRowOrder] = useState([]);

  // Initialize row order from storage or incoming tasks
  useEffect(() => {
    const stored = localStorage.getItem(rowOrderKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setRowOrder(parsed);
          return;
        }
      } catch (e) {
        console.warn('Failed to parse row order', e);
      }
    }
    // default: preserve incoming order
    const ids = tasks.map(t => `${t.itemType}-${t.itemId}`);
    setRowOrder(ids);
  }, [tasks, rowOrderKey]);

  const saveRowOrder = (order) => {
    setRowOrder(order);
    localStorage.setItem(rowOrderKey, JSON.stringify(order));
  };

  const orderedTasks = React.useMemo(() => {
    const map = new Map();
    tasks.forEach(t => map.set(`${t.itemType}-${t.itemId}`, t));
    const list = [];
    rowOrder.forEach(id => {
      if (map.has(id)) list.push(map.get(id));
    });
    // append any new tasks not in order yet
    tasks.forEach(t => {
      const id = `${t.itemType}-${t.itemId}`;
      if (!rowOrder.includes(id)) list.push(t);
    });
    return list;
  }, [tasks, rowOrder]);

  // Helper: Count visible days between two dates (excludes weekends if hideWeekends is true)
  const countVisibleDaysBetween = (fromDate, toDate) => {
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(toDate);
    to.setHours(0, 0, 0, 0);
    
    if (from >= to) return 0;
    
    let count = 0;
    const current = new Date(from);
    
    while (current < to) {
      if (hideWeekends) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++;
        }
      } else {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return count;
  };

  // Calculate task position and width
  const getTaskStyle = (task, previewDates = null) => {
    // Use preview dates if provided, otherwise use task's actual dates
    const startDate = previewDates ? previewDates.start : (task.start || task.startDate);
    const endDate = previewDates ? previewDates.end : (task.end || task.endDate);
    
    if (!startDate || !endDate || dateColumns.length === 0 || !visibleStart || !visibleEnd) {
      return { display: 'none' };
    }

    // Normalize all dates to midnight to avoid time component issues
    const taskStart = new Date(startDate);
    taskStart.setHours(0, 0, 0, 0);
    
    const taskEnd = new Date(endDate);
    taskEnd.setHours(0, 0, 0, 0);
    
    const firstColumn = new Date(visibleStart);
    firstColumn.setHours(0, 0, 0, 0);
    
    const lastColumn = new Date(visibleEnd);
    lastColumn.setHours(0, 0, 0, 0);

    // If completely outside visible range, hide
    if (taskEnd < firstColumn || taskStart > lastColumn) {
      return { display: 'none' };
    }

    // Clamp to visible window to avoid rendering across months
    const clampedStart = taskStart < firstColumn ? firstColumn : taskStart;
    const clampedEnd = taskEnd > lastColumn ? lastColumn : taskEnd;

    // Calculate position based on visible columns (accounting for hidden weekends)
    const startDiff = countVisibleDaysBetween(firstColumn, clampedStart);
    const taskDuration = countVisibleDaysBetween(clampedStart, clampedEnd);

    const left = Math.max(0, startDiff * columnWidth);
    const width = Math.max(columnWidth, Math.max(1, taskDuration) * columnWidth);

    let backgroundColor = '#8B5CF6';
    const itemType = task.itemType || task.type;
    
    if (itemType === 'Meeting') backgroundColor = '#EC4899';
    else if (itemType === 'Milestone') backgroundColor = '#F97316';
    else if (itemType === 'CompanyTask' || itemType === 'task') backgroundColor = '#3B82F6';
    else if (task.color) backgroundColor = task.color;

    return {
      left: `${left}px`,
      width: `${width}px`,
      backgroundColor,
    };
  };

  // Fetch resources
  useEffect(() => {
    async function fetchResources() {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/users', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setResources(data);
        }
      } catch (error) {
        console.error('Failed to fetch resources:', error);
      }
    }
    fetchResources();
  }, []);

  const goToPreviousMonth = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 28);
      setCurrentDate(newDate);
    }
  };

  const goToNextMonth = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 28);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDrop = (e, dateCol) => {
    e.preventDefault();
    e.stopPropagation();
    
    handleDragEnd(); // Reset drag state
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (!jsonData) return;
      
      const data = JSON.parse(jsonData);
      const dropDate = dateCol.date.toISOString().split('T')[0];
      
      console.log('[GANTT DROP]', {
        droppedOn: dropDate,
        dateObj: dateCol.date,
        dayOfWeek: dateCol.dayName,
        label: dateCol.label,
        item: data
      });
      
      if (data && onItemDrop) {
        onItemDrop(data, dropDate);
      }
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  };

  const handleDragOver = (e, dateCol) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    
    // Track that external dragging is happening
    if (!isExternalDragging) {
      setIsExternalDragging(true);
    }
    
    if (dateCol) {
      setDragOverDate(dateCol.date.toISOString().split('T')[0]);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverDate(null);
  };
  
  const handleDragEnd = () => {
    setIsExternalDragging(false);
    setDragOverDate(null);
  };

  // For seamless scroll, let native horizontal scroll do the work; no auto navigation.

  // Context menu
  const handleContextMenu = (e, task) => {
    e.preventDefault();
    
    // Calculate position to keep menu within viewport
    const menuWidth = 280; // Estimated menu width
    const menuHeight = 350; // Estimated menu height
    const padding = 10; // Padding from viewport edges
    
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust if menu would go off right edge
    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding;
    }
    
    // Adjust if menu would go off bottom edge
    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding;
    }
    
    // Ensure menu doesn't go off left or top edge
    if (x < padding) x = padding;
    if (y < padding) y = padding;
    
    setContextMenu({
      x,
      y,
      task,
    });
  };

  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', closeMenu);
      return () => document.removeEventListener('click', closeMenu);
    }
  }, [contextMenu]);

  const assignResourceToTask = async (task, user, isAssigned) => {
    if (onAssignResource) {
      await onAssignResource(task, user, isAssigned);
      // Update local state to reflect change immediately (optimistic update)
      setContextMenu(prev => prev ? {
        ...prev,
        task: {
          ...prev.task,
          assignedUserIds: isAssigned 
            ? (prev.task.assignedUserIds || []).filter(id => id !== user.id)
            : [...(prev.task.assignedUserIds || []), user.id]
        }
      } : null);
    } else {
      console.log('Assigning', user.name, 'to', task.name);
    }
    // Don't close menu - let user select multiple
  };

  // DRAG HANDLERS
  const startDrag = (e, task, action) => {
    e.preventDefault();
    e.stopPropagation();
    
    const taskUniqueId = `${task.itemType}-${task.itemId}`;
    const startDate = task.start || task.startDate;
    const endDate = task.end || task.endDate;
    const barRect = e.currentTarget?.getBoundingClientRect?.();
    const offsetPx = barRect ? Math.max(0, e.clientX - barRect.left) : 0;
    const maxOffsetDays = barRect ? Math.max(0, Math.ceil(barRect.width / columnWidth) - 1) : 0;
    const grabOffsetDays = Math.min(Math.floor(offsetPx / columnWidth), maxOffsetDays);
    const durationVisibleDays = countVisibleDaysBetween(startDate, endDate);
    
    console.log(`[DRAG START] Task: ${taskUniqueId}, Action: ${action}`);
    
    setDragState({
      taskUniqueId,
      task,
      action,
      startX: e.clientX,
      originalStart: startDate,
      originalEnd: endDate,
      previewStart: startDate,
      previewEnd: endDate,
      grabOffsetDays,
      durationVisibleDays,
    });
  };

  // Helper: Add visible days to a date, accounting for hidden weekends
  // When hideWeekends is true, we need to skip weekends when counting days
  const addVisibleDays = (fromDate, visibleDaysToAdd) => {
    const result = new Date(fromDate);
    let remaining = Math.abs(visibleDaysToAdd);
    const direction = visibleDaysToAdd >= 0 ? 1 : -1;
    
    while (remaining > 0) {
      result.setDate(result.getDate() + direction);
      // If hideWeekends is on, skip weekends in the count
      if (hideWeekends) {
        const dayOfWeek = result.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          // It's a weekday, count it
          remaining--;
        }
        // If it's a weekend, don't count it but still move
      } else {
        // Weekends visible, count every day
        remaining--;
      }
    }
    
    return result;
  };

  const getColumnIndexFromClientX = (clientX) => {
    if (!timelineHeaderRef.current) return null;
    const rect = timelineHeaderRef.current.getBoundingClientRect();
    const scrollLeft = ganttContainerRef.current?.scrollLeft || 0;
    const x = clientX - rect.left + scrollLeft;
    if (x < 0) return null;
    const index = Math.floor(x / columnWidth);
    if (index < 0 || index >= dateColumns.length) return null;
    return index;
  };

  const onMouseMove = (e) => {
    if (!dragState) return;
    
    const origStart = new Date(dragState.originalStart);
    const origEnd = new Date(dragState.originalEnd);
    
    let newStart = new Date(origStart);
    let newEnd = new Date(origEnd);
    
    if (dragState.action === 'move') {
      const columnIndex = getColumnIndexFromClientX(e.clientX);
      if (columnIndex === null) return;
      const targetIndex = Math.max(0, Math.min(dateColumns.length - 1, columnIndex - (dragState.grabOffsetDays || 0)));
      const targetDate = dateColumns[targetIndex]?.date;
      if (!targetDate) return;
      newStart = new Date(targetDate);
      newStart.setHours(0, 0, 0, 0);
      newEnd = addVisibleDays(newStart, dragState.durationVisibleDays || 0);
    } else if (dragState.action === 'resize-start') {
      const deltaX = e.clientX - dragState.startX;
      const dayWidthPx = columnWidth;
      const columnsDelta = Math.round(deltaX / dayWidthPx);
      if (columnsDelta === 0) return;
      newStart = addVisibleDays(origStart, columnsDelta);
      if (newStart >= origEnd) {
        newStart = new Date(origEnd);
        newStart.setDate(newStart.getDate() - 1);
      }
    } else if (dragState.action === 'resize-end') {
      const deltaX = e.clientX - dragState.startX;
      const dayWidthPx = columnWidth;
      const columnsDelta = Math.round(deltaX / dayWidthPx);
      if (columnsDelta === 0) return;
      newEnd = addVisibleDays(origEnd, columnsDelta);
      if (newEnd <= origStart) {
        newEnd = new Date(origStart);
        newEnd.setDate(newEnd.getDate() + 1);
      }
    }
    
    setDragState(prev => ({
      ...prev,
      previewStart: newStart.toISOString().split('T')[0],
      previewEnd: newEnd.toISOString().split('T')[0]
    }));
  };

  const onMouseUp = () => {
    if (!dragState) return;
    
    const { task, originalStart, originalEnd, previewStart, previewEnd } = dragState;
    
    console.log(`[DRAG END] Task: ${task.itemId}, Original: ${originalStart} to ${originalEnd}, Preview: ${previewStart} to ${previewEnd}`);
    
    // Only save if dates actually changed
    if (previewStart !== originalStart || previewEnd !== originalEnd) {
      console.log(`[API CALL] Saving task ${task.itemId}`);
      
      if (onTaskDateChange) {
        onTaskDateChange(task, previewStart, previewEnd);
      }
    }
    
    setDragState(null);
  };

  // Global mouse listeners
  useEffect(() => {
    if (dragState) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [dragState]);

  // Row drag state - track which row is being dragged
  const [draggedRowId, setDraggedRowId] = useState(null);

  // Row drag helpers
  const handleRowDragStart = (e, id) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    // Store in state since dataTransfer is not accessible during dragover
    setDraggedRowId(id);
    // Add visual feedback
    e.target.closest('.gantt-task-row')?.classList.add('dragging');
  };

  const handleRowDragEnd = (e) => {
    setDraggedRowId(null);
    // Remove visual feedback from all rows
    document.querySelectorAll('.gantt-task-row.dragging').forEach(el => {
      el.classList.remove('dragging');
    });
    document.querySelectorAll('.gantt-task-row.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });
  };

  const reorderList = (list, fromId, toId) => {
    if (fromId === toId) return list;
    const current = [...list];
    const fromIdx = current.indexOf(fromId);
    const toIdx = current.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return current;
    current.splice(fromIdx, 1);
    current.splice(toIdx, 0, fromId);
    return current;
  };

  const handleRowDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Use state instead of dataTransfer (which is restricted during dragover)
    if (!draggedRowId || draggedRowId === targetId) return;
    
    // Add visual feedback for drop target
    const targetRow = e.target.closest('.gantt-task-row');
    if (targetRow && !targetRow.classList.contains('drag-over')) {
      document.querySelectorAll('.gantt-task-row.drag-over').forEach(el => {
        el.classList.remove('drag-over');
      });
      targetRow.classList.add('drag-over');
    }
  };

  const handleRowDrop = (e, targetId) => {
    e.preventDefault();
    
    if (!draggedRowId || draggedRowId === targetId) {
      setDraggedRowId(null);
      return;
    }
    
    const newOrder = reorderList(rowOrder, draggedRowId, targetId);
    if (newOrder !== rowOrder) {
      saveRowOrder(newOrder);
    }
    
    setDraggedRowId(null);
    
    // Clean up visual feedback
    document.querySelectorAll('.gantt-task-row.dragging, .gantt-task-row.drag-over').forEach(el => {
      el.classList.remove('dragging', 'drag-over');
    });
  };

  return (
    <div className="timeline-gantt">
      {/* Header */}
      <div className="gantt-header">
        <div className="gantt-controls">
          <button onClick={goToPreviousMonth} className="gantt-nav-btn">◀</button>
          <button onClick={goToToday} className="gantt-today-btn">Today</button>
          <button onClick={goToNextMonth} className="gantt-nav-btn">▶</button>
          <h2 className="gantt-title">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
        </div>

        <div className="gantt-view-modes">
          <button className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>
            Month
          </button>
          <button className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>
            4 Weeks
          </button>
          <button className={hideWeekends ? 'active' : ''} onClick={() => setHideWeekends(!hideWeekends)}>
            📅 {hideWeekends ? 'Show Weekends' : 'Hide Weekends'}
          </button>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="gantt-container" ref={ganttContainerRef}>
        <div className="gantt-grid">
          <div className="gantt-row gantt-header-row">
            <div className="gantt-sidebar-header">Company / Branch / Task</div>
            <div
              className="gantt-timeline-header"
              style={{ gridTemplateColumns: `repeat(${dateColumns.length}, ${columnWidth}px)` }}
              ref={timelineHeaderRef}
            >
              {dateColumns.map((col, idx) => (
                <div key={idx} className={`gantt-date-cell ${col.isWeekend ? 'weekend' : ''} ${col.isToday ? 'today' : ''}`}>
                  {col.showMonthLabel && (
                    <div className="gantt-month-label">{col.monthName}</div>
                  )}
                  <div className="gantt-date-label">{col.label}</div>
                  <div className="gantt-day-label">{col.dayName}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Task Rows - ONE ROW PER TASK with manual reordering */}
          {orderedTasks.map((task) => {
            const taskUniqueId = `${task.itemType}-${task.itemId}`;
            const isDragging = dragState && dragState.taskUniqueId === taskUniqueId;
            const previewDates = isDragging
              ? { start: dragState.previewStart, end: dragState.previewEnd }
              : null;
            const style = getTaskStyle(task, previewDates);
            const companyName = task.companyName || task.Company?.name || 'Unknown Company';
            const branchName = task.branchName || task.Branch?.name || 'Company-wide';

            if (!style || style.display === 'none') return null;

            return (
              <div key={taskUniqueId} className="gantt-group">
                <div
                  className={`gantt-task-row ${draggedRowId === taskUniqueId ? 'dragging' : ''}`}
                  onDrop={(e) => handleRowDrop(e, taskUniqueId)}
                  onDragOver={(e) => handleRowDragOver(e, taskUniqueId)}
                  onDragLeave={(e) => {
                    e.target.closest('.gantt-task-row')?.classList.remove('drag-over');
                  }}
                >
                  <div className="gantt-sidebar">
                    <div
                      className="row-drag-handle"
                      draggable
                      onDragStart={(e) => handleRowDragStart(e, taskUniqueId)}
                      onDragEnd={handleRowDragEnd}
                      title="Drag to reorder row"
                    >
                      ☰
                    </div>
                    <div className="gantt-task-label">
                      <div className="gantt-task-title">
                        {task.itemType === 'company' ? `🏢 ${task.name || task.title}` : (task.name || task.title)}
                      </div>
                      <div className="gantt-task-subtitle">
                        {task.itemType === 'company' 
                          ? `${new Date(task.start).toLocaleDateString()} - ${new Date(task.end).toLocaleDateString()}`
                          : `${companyName} • ${branchName}`
                        }
                      </div>
                    </div>
                  </div>
                  <div
                    className="gantt-timeline-row"
                    style={{ gridTemplateColumns: `repeat(${dateColumns.length}, ${columnWidth}px)` }}
                  >
                    {/* Grid cells */}
                    {dateColumns.map((col, idx) => {
                      const dateStr = col.date.toISOString().split('T')[0];
                      const isHighlighted = dragOverDate === dateStr;
                      
                      return (
                        <div
                          key={idx}
                          className={`gantt-grid-cell ${col.isWeekend ? 'weekend' : ''} ${col.isToday ? 'today' : ''} ${isHighlighted ? 'drag-over-highlight' : ''}`}
                          onDrop={(e) => handleDrop(e, col)}
                          onDragOver={(e) => handleDragOver(e, col)}
                          onDragLeave={handleDragLeave}
                        />
                      );
                    })}
                    
                    {/* Single task bar for this row */}
                    {style && style.display !== 'none' && (
                      <div className="gantt-task-bar-container">
                        <div
                          className={`gantt-task-bar ${isDragging ? 'is-moving' : ''} ${task.itemType === 'company' ? 'company-bar' : ''}`}
                          style={{
                            ...style,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: task.itemType === 'company' ? 'pointer' : (isDragging ? 'grabbing' : 'grab'),
                            opacity: isDragging ? 0.7 : 1,
                            zIndex: isDragging ? 10 : 1
                          }}
                          onClick={() => !isDragging && onTaskClick(task)}
                          onContextMenu={(e) => task.itemType !== 'company' && handleContextMenu(e, task)}
                          onMouseDown={(e) => {
                            // Don't allow dragging company items - they are read-only summaries
                            if (task.itemType === 'company') return;
                            // Don't start move if clicking on resize handles
                            if (!e.target.classList.contains('gantt-task-resize-handle')) {
                              startDrag(e, task, 'move');
                            }
                          }}
                          title={task.itemType === 'company' 
                            ? `${task.name} - Click to view company details`
                            : (task.name || task.title)
                          }
                        >
                          {/* Left resize handle - hide for company items */}
                          {task.itemType !== 'company' && (
                            <div
                              className="gantt-task-resize-handle gantt-task-resize-left"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                startDrag(e, task, 'resize-start');
                              }}
                              title="Drag to change start date"
                            />
                          )}
                          
                          {/* Progress bar */}
                          {(task.setupProgress !== undefined && task.setupProgress !== null) && (
                            <div 
                              className="gantt-task-progress"
                              style={{ width: `${task.setupProgress}%` }}
                            />
                          )}
                          
                          <div className="gantt-task-content">
                            <span className="gantt-task-name">{task.name || task.title}</span>
                            {task.setupProgress !== undefined && task.setupProgress !== null && (
                              <span className="gantt-task-progress-text">{task.setupProgress}%</span>
                            )}
                            {task.status && (
                              <span className="gantt-task-status">{task.status}</span>
                            )}
                            {(task.assignedUsers?.length > 0 || task.assignedUser) && (
                              <span className="gantt-task-user">
                                👤 {task.assignedUsers?.length > 0 
                                  ? task.assignedUsers.map(u => u.name).join(', ')
                                  : task.assignedUser?.name}
                              </span>
                            )}
                          </div>
                          
                          {/* Right resize handle - hide for company items */}
                          {task.itemType !== 'company' && (
                            <div
                              className="gantt-task-resize-handle gantt-task-resize-right"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                startDrag(e, task, 'resize-end');
                              }}
                              title="Drag to change end date"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Drop zone row - Sticky at bottom */}
          <div className="gantt-group" style={{ 
            position: 'sticky', 
            bottom: 0, 
            backgroundColor: 'white',
            zIndex: 5,
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
          }}>
            <div className="gantt-task-row" style={{ minHeight: '80px', borderTop: '2px dashed #E5E7EB' }}>
              <div className="gantt-sidebar">
                <div className="gantt-task-label">
                  <div className="gantt-task-title" style={{ color: '#9CA3AF', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    📍 Drop a service or branch here to add
                  </div>
                </div>
              </div>
              <div className="gantt-timeline-row" style={{ gridTemplateColumns: `repeat(${dateColumns.length}, ${columnWidth}px)` }}>
                {dateColumns.map((col, idx) => {
                  const dateStr = col.date.toISOString().split('T')[0];
                  const isHighlighted = dragOverDate === dateStr;
                  
                  return (
                    <div
                      key={idx}
                      className={`gantt-grid-cell ${col.isWeekend ? 'weekend' : ''} ${col.isToday ? 'today' : ''} ${isHighlighted ? 'drag-over-highlight' : ''}`}
                      onDrop={(e) => handleDrop(e, col)}
                      onDragOver={(e) => handleDragOver(e, col)}
                      onDragLeave={handleDragLeave}
                      style={{ 
                        backgroundColor: isHighlighted ? '#E0F2FE' : 'transparent',
                        border: '1px dashed #D1D5DB',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {isHighlighted && (
                        <div style={{
                          fontSize: '10px',
                          color: '#3B82F6',
                          textAlign: 'center',
                          marginTop: '8px',
                          fontWeight: '600'
                        }}>
                          Drop here
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu - Multi-select */}
      {contextMenu && (
        <div 
          className="context-menu" 
          style={{ 
            position: 'fixed',
            top: contextMenu.y, 
            left: contextMenu.x,
            zIndex: 9999
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header">
            <strong>{contextMenu.task.name || contextMenu.task.title}</strong>
          </div>
          <div className="context-menu-section">
            <div className="context-menu-title">Assign Resources (Multi-select)</div>
            <div className="context-menu-items">
              {resources.length > 0 ? (
                resources.map(user => {
                  const assignedIds = contextMenu.task.assignedUserIds || 
                    (contextMenu.task.assignedUserId ? [contextMenu.task.assignedUserId] : []);
                  const isAssigned = assignedIds.includes(user.id);
                  return (
                    <div
                      key={user.id}
                      className={`context-menu-item ${isAssigned ? 'selected' : ''}`}
                      onClick={() => assignResourceToTask(contextMenu.task, user, isAssigned)}
                    >
                      <input 
                        type="checkbox" 
                        checked={isAssigned}
                        onChange={() => {}}
                        style={{ marginRight: '8px', pointerEvents: 'none' }}
                      />
                      {user.name} - {user.role}
                    </div>
                  );
                })
              ) : (
                <div className="context-menu-item disabled">No resources available</div>
              )}
            </div>
          </div>
          <div className="context-menu-footer">
            <button 
              className="context-menu-done-btn"
              onClick={() => setContextMenu(null)}
            >
              Done
            </button>
          </div>
        </div>
      )}
      
      {/* Floating Drop Indicator */}
      {isExternalDragging && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '20px 40px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          fontSize: '18px',
          fontWeight: '600',
          zIndex: 9998,
          pointerEvents: 'none',
          animation: 'pulse 2s infinite'
        }}>
          📍 Drop anywhere on the timeline to add this item
        </div>
      )}
    </div>
  );
}

export default TimelineGantt;
